
import { useState, useMemo } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SearchBar from '../../components/base/SearchBar';
import Button from '../../components/base/Button';
import { catsData } from '../../mocks/cats';

export default function Adoption() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGender, setSelectedGender] = useState('all');
  const [selectedAge, setSelectedAge] = useState('all');

  const filteredCats = useMemo(() => {
    return catsData.filter(cat => {
      const matchesSearch = cat.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           cat.breed.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           cat.color.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           cat.personality.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesGender = selectedGender === 'all' || cat.gender.toLowerCase() === selectedGender;
      
      const matchesAge = selectedAge === 'all' || 
                        (selectedAge === 'young' && parseInt(cat.age) <= 2) ||
                        (selectedAge === 'adult' && parseInt(cat.age) > 2 && parseInt(cat.age) <= 5) ||
                        (selectedAge === 'senior' && parseInt(cat.age) > 5);

      return matchesSearch && matchesGender && matchesAge;
    });
  }, [searchQuery, selectedGender, selectedAge]);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-orange-500 to-pink-500 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Find Your Perfect Companion</h1>
          <p className="text-xl mb-8">
            Browse our available cats and find the one that's meant to be part of your family
          </p>
          <div className="max-w-md mx-auto">
            <SearchBar 
              onSearch={setSearchQuery}
              placeholder="Search by name, breed, color, or personality..."
              className="bg-white"
            />
          </div>
        </div>
      </section>

      {/* Filters Section */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-4 items-center justify-center">
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Gender:</label>
              <select 
                value={selectedGender}
                onChange={(e) => setSelectedGender(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
              >
                <option value="all">All</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Age:</label>
              <select 
                value={selectedAge}
                onChange={(e) => setSelectedAge(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 pr-8"
              >
                <option value="all">All Ages</option>
                <option value="young">Young (1-2 years)</option>
                <option value="adult">Adult (3-5 years)</option>
                <option value="senior">Senior (6+ years)</option>
              </select>
            </div>

            <div className="text-sm text-gray-600">
              Showing {filteredCats.length} of {catsData.length} cats
            </div>
          </div>
        </div>
      </section>

      {/* Cats Grid */}
      <section className="py-12" data-product-shop>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filteredCats.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-search-line text-gray-400 text-6xl mb-4 w-16 h-16 flex items-center justify-center mx-auto"></i>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No cats found</h3>
              <p className="text-gray-500">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredCats.map((cat) => (
                <div key={cat.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                  <img 
                    src={cat.image}
                    alt={`${cat.name} - ${cat.breed}`}
                    className="w-full h-64 object-cover object-top"
                  />
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-xl font-semibold text-gray-900">{cat.name}</h3>
                      <span className="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                        {cat.age}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-user-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        {cat.gender} • {cat.breed}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-palette-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        {cat.color}
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <i className="ri-heart-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        {cat.personality.split(',')[0]}
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                      {cat.description}
                    </p>

                    <div className="border-t pt-4">
                      <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-medium text-gray-700">{cat.spayNeuter}</span>
                        <span className="text-lg font-bold text-orange-600">{cat.cost}</span>
                      </div>
                      
                      <Button className="w-full bg-orange-600 hover:bg-orange-700">
                        <i className="ri-heart-line mr-2 w-4 h-4 flex items-center justify-center"></i>
                        Adopt {cat.name}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Adoption Process */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Adoption Process</h2>
            <p className="text-xl text-gray-600">
              Our simple process ensures the best match for both you and your new feline friend
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">1</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Browse & Choose</h3>
              <p className="text-gray-600">Browse our available cats and find one that catches your heart</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">2</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Meet & Greet</h3>
              <p className="text-gray-600">Schedule a visit to meet your potential new companion</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">3</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Application</h3>
              <p className="text-gray-600">Complete our adoption application and reference check</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-orange-600">4</span>
              </div>
              <h3 className="text-lg font-semibold mb-2">Take Home</h3>
              <p className="text-gray-600">Complete the adoption and welcome your new family member</p>
            </div>
          </div>

          <div className="mt-16 bg-white p-8 rounded-lg shadow-md">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">Important Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Spay/Neuter Requirement</h4>
                <p className="text-gray-600">
                  All cats must be spayed or neutered before adoption. The cost is included in the adoption fee 
                  and will be arranged with our partner veterinarians.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What's Included</h4>
                <ul className="text-gray-600 space-y-1">
                  <li>• Spay/neuter surgery</li>
                  <li>• Current vaccinations</li>
                  <li>• Microchip identification</li>
                  <li>• Health check by veterinarian</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
